<template>
    <div :id="['lightbox-' + identifierId]"
         class="form-modal-container d-flex flex-column justify-content-center align-items-center h-100">
        <div class="w-100 h-100">
            <div class="form-overlay-background"></div>
            <div class="form-template7 d-flex flex-column form-internal-container"
                 :class="[formSizeClass, formPosition]"
                 :id="['form-template-' + identifierId]">
                <div class="container-form h-100">
                    <div class="row h-100 content-question">
                        <div class="col-12 h-100 position-relative">
                            <div class="row m-0"
                                 :class="['class-template-header-' + identifierId]">
                                <div class="col-12 container-logo"
                                     v-if="logo1 !== ''"
                                     v-html="logo1">
                                </div>
                                <div class="col-12 container-title">
                                    <p class="form-title">{{ formTitle }}</p>
                                    <p class="form-description my-2">{{ formDescription }}</p>
                                </div>
                            </div>
                            <div class="questions-container">
                                <div class="carousel slide" data-bs-wrap="false"
                                     :id="'carousel-' + identifierId">
                                    <form method="post" class="m-0"
                                          :name="'form-' + identifierId"
                                          :id="'form-' + identifierId"
                                          @submit.prevent="submitHandler">
                                        <Questions
                                            :key="identifierId"
                                            :additional-percentage-height="additionalPercentageHeight"
                                            :existValueOther="existValueOther"
                                            :form-size-class="formSizeClass"
                                            :has-pages="true"
                                            :hotspot="hotspot"
                                            :identifierId="identifierId"
                                            :input-text="inputText"
                                            :pages="pages"
                                            :page-active="pageActive"
                                            :question-image-required="questionImageRequired"
                                            :store="store"
                                            :translate-labels="translateLabels"
                                            :type-identifier="typeIdentifier"
                                            :has-submitted="hasSubmitted"
                                            @register-field="registerField"
                                            @field-change="handleFieldChange"
                                            @other-field-data="handleOtherFieldData"
                                        />
                                    </form>
                                </div>
                            </div>
                            <div class="row form-internal-footer m-0"
                                 :class="['class-template-footer-' + identifierId]">
                                <div class="row container-form-close"
                                     v-if="!formRequired">
                                    <div class="col">
                                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                                                @click="closeModal"></button>
                                    </div>
                                </div>
                                <div class="col-12 container-image m-0 p-0">
                                    <p class="text-mandatory">{{ translateLabels.textMandatoryFields }}</p>
                                    <div class="container-btn d-flex justify-content-sm-start justify-content-between"
                                         v-if="pages > 1">
                                        <button type="button" class="btn btn-secondary me-1 me-lg-3 mb-1 text-uppercase btn-back"
                                                v-bind:data-bs-target="'#carousel-' + identifierId" data-bs-slide="prev">{{ translateLabels.textBack }}</button>
                                        <button type="button" class="btn btn-primary mb-1 text-uppercase btn-next"
                                                v-bind:data-bs-target="'#carousel-' + identifierId" data-bs-slide="next">{{ translateLabels.textNext }}</button>
                                        <button type="button" class="btn btn-primary mb-1 text-uppercase d-none btn-submit"
                                                @click.prevent="submitHandler">{{ translateLabels.textButtonSubmit }}</button>
                                    </div>
                                    <div class="container-btn d-flex justify-content-sm-start justify-content-between"
                                         v-if="pages == 1">
                                        <button type="button" class="btn btn-primary mb-1 text-uppercase btn-submit"
                                                @click.prevent="submitHandler">{{ translateLabels.textButtonSubmit }}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useForm } from 'vee-validate';
import { usePlayerStore } from '@/playerVue3/storage/storePlayer';
import Questions from "@/playerVue3/components/form/questions.vue";
import { prepareDataAndSave, replaceDataVariableInForm } from "@/playerVue3/services/form-service";

// INITIALIZE STORE & COMPOSABLES
const playerStore = usePlayerStore();

// PROPS & EMITS
const props = defineProps({
    configurationData: {
        type: String,
        required: false,
    },
    formDescription: {
        type: String,
        required: true,
    },
    formPosition: {
        type: String,
        required: false,
        default: '',
    },
    formRequired: {
        type: Boolean,
        required: false,
        default: false,
    },
    formSizeClass: {
        type: String,
        required: false,
        default: 'large-form-modal-container',
    },
    formTitle: {
        type: String,
        required: true,
    },
    hotspot: {
        type: Object,
        required: false,
    },
    hotspotEmailTemplateId: {
        type: Number,
        required: false,
    },
    identifierId: {
        type: Number,
        required: true,
    },
    store: {
        type: Object,
        required: true
    },
    translateLabels: {
        type: Object,
        required: true,
    },
    typeIdentifier: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['setClose']);

// VeeValidate form setup con configuración lazy
const { validate, values, errors, setFieldValue, submitCount } = useForm({
    validateOnMount: false,    // No validar al montar
    initialValues: {},
});

// REACTIVE VARIABLES
const additionalPercentageHeight = ref(0);
const existValueOther = ref(false);
const inputText = ref(false);
const questionImageRequired = ref(false);
const pageActive = ref(1);
const pages = ref(0);
const myCarousel = ref('');
const itemFirst = ref('');
const itemLast = ref('');
const hasSubmitted = ref(false); // Flag para rastrear si ya se envió
const otherFieldsData = ref({}); // Nuevo: almacenar datos de campos "other"

const fields = ref({
    formID: playerStore.getFormID,
    formIsFromAttrAdditional: false,
    hotspotEmailTemplateID: props.hotspotEmailTemplateId,
    hotspotID: props.identifierId,
    vID: playerStore.getVideoID,
    wlID: playerStore.getWeblinkID,
});

// COMPUTED PROPERTIES
const logo1 = computed(() => {
    if (props.configurationData) {
        const configurationData = JSON.parse(props.configurationData);
        if (configurationData.logo1) {
            const weblinkID = playerStore.getWeblinkID;
            if (configurationData.logo1.variableName !== '' && weblinkID > 0) {
                return replaceDataVariableInForm(playerStore, props.hotspot, configurationData.logo1);
            } else {
                return '<img class="img-fluid img-logo" alt="V2M" src="'+ configurationData.logo1.url + '">';
            }
        }
    }
    return '';
});

// METHODS
const closeModal = () => {
    emit('setClose');
};

const registerField = (fieldName, fieldValue = '') => {
    setFieldValue(fieldName, fieldValue, false); // false = no validar inmediatamente
};

const handleFieldChange = (fieldName, value) => {
    setFieldValue(fieldName, value, hasSubmitted.value); // Solo validar si ya se envió
};

const handleOtherFieldData = (data) => {
    console.log('handleOtherFieldData', data);
    const key = `question_${data.quetionType}_${data.questionId}_${data.optionId}_text_other`;

    if (data.isSelected && data.otherValue) {
        otherFieldsData.value[key] = data.otherValue;
    } else {
        delete otherFieldsData.value[key];
    }
    console.log('otherFieldsData', otherFieldsData)
};

const submitHandler = async () => {
    hasSubmitted.value = true;

    // Disparar validación
    const { valid } = await validate();

    if (valid) {
        // Serializar datos del formulario
        const formDataSerialize = Object.entries(values).map(([name, value]) => ({
            name,
            value
        }));

        // Agregar datos adicionales de campos "other"
        Object.entries(otherFieldsData.value).forEach(([key, data]) => {
            formDataSerialize.push({
                name: key,
                value: data
            });
        });

        // necesito ordenar el contenido de formDataSerialize por el name para que los campos other esten al final
        formDataSerialize.sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        });

        await prepareDataAndSave(playerStore, fields.value, formDataSerialize);
        closeModal();
    }
};

// LIFECYCLE HOOKS
(async () => {
    pages.value = props.hotspot ? props.hotspot.pagesForm : 1;

    if (pages.value > 1) {
        myCarousel.value = $('#carousel-' + props.identifierId);
        itemFirst.value = myCarousel.value.find('#carousel-' + props.identifierId + ' .carousel-inner > .carousel-item:first-child');
        itemLast.value = myCarousel.value.find('#carousel-' + props.identifierId + ' .carousel-inner > .carousel-item:last-child');
        const carouselLength = $('#carousel-' + props.identifierId + ' .carousel-inner > .carousel-item').length - 1;
        myCarousel.value.carousel({
            interval: false,
            wrap: false
        }).on('slide.bs.carousel', function (e) {
            pageActive.value = (e.to + 1);
            if (e.to == carouselLength) {
                $('.btn-submit').removeClass('d-none');
                $('.btn-next').addClass('d-none');
                console.log('next me oculto');
            } else if (e.to == 0) {
                $('.btn-submit').addClass('d-none');
                $('.btn-next').removeClass('d-none');
            } else {
                $('.btn-submit').addClass('d-none');
                $('.btn-next').removeClass('d-none');
            }
        });
    }
})();
</script>
